import {
  PropsWithChildren,
  useMemo,
  createContext,
  useContext,
  useState,
} from 'react';
import { brokerageDetailsRefName } from '../../components/BrokerDetails/sections/FirmDetails/SubSections/BrokerageDetails';
import { representativeRefName } from '../../components/BrokerDetails/sections/FirmDetails/SubSections/Representative';
import { bankDetailsRefName } from '../../components/BrokerDetails/sections/FirmDetails/SubSections/BankDetails';
import { principalCompanyDetailsRefName } from '../../components/BrokerDetails/sections/FirmDetails/SubSections/PrincipalCompanyDetails';
import { SystemSettingsSectionRefName } from '../../components/BrokerDetails/sections/SystemSettings/SubSections/SystemSettings';
import { notificationsRefName } from '../../components/BrokerDetails/sections/SystemSettings/SubSections/Notifications';
import { CommercialsSectionRefName } from '../../components/BrokerDetails/sections/Commercials';
import { defaultCompany } from './Company';
import { useCompany } from './useCompany';
import { Brokerage } from '../brokerages';

export type CompanyContextType = {
  company: Brokerage,
  fetchCompany(): Promise<void>,
  updateCompany(field: string, value: any): Promise<void>,
  sectionLockedStatus: Record<string, boolean>,
  setLockedStatus(section: string, isLocked: boolean): void,
}

const defaultBrokeragesContext: CompanyContextType = {
  company: defaultCompany,
  sectionLockedStatus: {},
  fetchCompany: async () => { },
  updateCompany: async () => { },
  setLockedStatus: () => { },
};

export const CompanyContext = createContext<CompanyContextType>(
  defaultBrokeragesContext,
);

export function useCompanyContext() {
  return useContext(CompanyContext);
}

const BrokerDetailSubSection = [
  brokerageDetailsRefName,
  representativeRefName,
  bankDetailsRefName,
  principalCompanyDetailsRefName,
  SystemSettingsSectionRefName,
  notificationsRefName,
  CommercialsSectionRefName,
];

export function CompanyProvider({ id, children }: PropsWithChildren<{ id: string }>) {
  const company = useCompany(id);

  const [sectionLockedStatus, setSectionLockedStatus] = useState<Record<string, boolean>>(BrokerDetailSubSection.reduce((acc, curr) => ({ ...acc, [curr]: true }), {}));

  const setLockedStatus = (section: string, isLocked: boolean) => {
    setSectionLockedStatus({ ...sectionLockedStatus, ...{ [section]: isLocked } });
  };

  const contextValue = useMemo(() => ({ ...company, setLockedStatus, sectionLockedStatus }), [company, sectionLockedStatus]);

  return (
    <CompanyContext.Provider value={contextValue}>
      {children}
    </CompanyContext.Provider>
  );
}
